import {
  DIFFICULTIES,
  IMAGE_REQUEST_STATUSES,
  QUESTION_STATUSES,
  QUESTION_TYPES,
  YEAR_PARTS,
  answerKeySchema,
  questionContentSchema,
} from '@oefy/core';
import { z } from 'zod';
import { gameKeySchema, schoolGroupSchema, timestampSchema } from './common';
import { imageKeySchema } from './images';

export const difficultySchema = z.enum(DIFFICULTIES);
export const yearPartSchema = z.enum(YEAR_PARTS);
export const contentStatusSchema = z.enum(QUESTION_STATUSES);
export const questionTypeSchema = z.enum(QUESTION_TYPES);
export const subjectKeySchema = z.string().min(1).max(40);
export const subjectFormatSchema = z.enum(['standard', 'reading']);

export const difficultyCountsSchema = z.object({
  easy: z.int().min(0),
  medium: z.int().min(0),
  hard: z.int().min(0),
});

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullable()
    .transform((value) => (value === '' ? null : value));

const pageQuery = {
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
};

const queryGroup = z.coerce.number().pipe(schoolGroupSchema).optional();

export const topicSchema = z.object({
  id: z.uuid(),
  subjectKey: z.string(),
  name: z.string(),
  sortOrder: z.int(),
  schoolGroups: z.array(schoolGroupSchema),
});

export const subtopicSchema = z.object({
  id: z.uuid(),
  topicId: z.uuid(),
  name: z.string(),
  sortOrder: z.int(),
  schoolGroups: z.array(schoolGroupSchema),
});

const usageFields = {
  questions: z.int().min(0),
  published: z.int().min(0),
  groups: z.array(schoolGroupSchema),
};

export const subtopicUsageSchema = subtopicSchema.extend(usageFields);

export const topicUsageSchema = topicSchema.extend({
  ...usageFields,
  unsorted: z.int().min(0),
  subtopics: z.array(subtopicUsageSchema),
});

export const subjectSchema = z.object({
  key: z.string(),
  name: z.string(),
  format: subjectFormatSchema,
  topics: z.array(topicUsageSchema),
});

export const subjectListSchema = z.object({ subjects: z.array(subjectSchema) });

export const topicInputSchema = z.object({
  subjectKey: subjectKeySchema,
  name: z.string().trim().min(1).max(80),
});

export const topicUpdateSchema = z.object({
  name: z.string().trim().min(1).max(80),
  sortOrder: z.int().min(0).max(999),
});

export const subtopicInputSchema = z.object({
  topicId: z.uuid(),
  name: z.string().trim().min(1).max(80),
});

export const subtopicUpdateSchema = topicUpdateSchema;

export const questionInputSchema = z.object({
  schoolGroup: schoolGroupSchema,
  subjectKey: subjectKeySchema,
  topicId: z.uuid().nullable(),
  subtopicId: z.uuid().nullable().default(null),
  readingTextId: z.uuid().nullable(),
  instruction: optionalText(200),
  context: optionalText(2000),
  prompt: z.string().trim().min(1).max(1000),
  imageKey: imageKeySchema.nullable(),
  imageAlt: optionalText(200).default(null),
  content: questionContentSchema,
  answerKey: answerKeySchema,
  explanation: z.string().trim().min(1).max(2000),
  difficulty: difficultySchema.nullable(),
  yearPart: yearPartSchema.nullable().default(null),
});

export const questionImageSchema = z.object({
  status: z.enum(IMAGE_REQUEST_STATUSES),
  description: z.string(),
  error: z.string().nullable(),
  requestedAt: timestampSchema,
});

export const questionImageInputSchema = z.object({
  description: z.string().trim().max(500).nullable().default(null),
});

export const questionImageRetrySchema = z.object({
  ids: z.array(z.uuid()).min(1).max(100).nullable().default(null),
});

export const questionSummarySchema = z.object({
  id: z.uuid(),
  schoolGroup: schoolGroupSchema,
  subjectKey: z.string(),
  topicId: z.uuid().nullable(),
  topicName: z.string().nullable(),
  subtopicId: z.uuid().nullable(),
  subtopicName: z.string().nullable(),
  readingTextId: z.uuid().nullable(),
  type: questionTypeSchema,
  status: contentStatusSchema,
  prompt: z.string(),
  difficulty: difficultySchema.nullable(),
  yearPart: yearPartSchema.nullable(),
  aiDifficulty: difficultySchema.nullable(),
  thumbnailKey: z.string().nullable(),
  answerPreview: z.string(),
  openReports: z.int().min(0),
  answered: z.int().min(0),
  correctRate: z.number().min(0).max(1).nullable(),
  image: questionImageSchema.nullable(),
  updatedAt: timestampSchema,
});

export const questionDetailSchema = questionSummarySchema.extend({
  instruction: z.string().nullable(),
  context: z.string().nullable(),
  imageKey: z.string().nullable(),
  imageAlt: z.string().nullable(),
  content: questionContentSchema,
  answerKey: answerKeySchema,
  explanation: z.string(),
  aiDifficultyScore: z.number().min(0).max(4).nullable(),
  aiDifficultyPending: z.boolean(),
  createdAt: timestampSchema,
  publishedAt: timestampSchema.nullable(),
  archivedAt: timestampSchema.nullable(),
});

export const QUESTION_ATTENTION = [
  'ai_pending',
  'ai_differs',
  'reported',
  'image_pending',
  'image_failed',
] as const;

export const questionAttentionSchema = z.enum(QUESTION_ATTENTION);

export const questionListQuerySchema = z.object({
  schoolGroup: queryGroup,
  subjectKey: subjectKeySchema.optional(),
  topicId: z.uuid().optional(),
  subtopicId: z.uuid().optional(),
  readingTextId: z.uuid().optional(),
  type: questionTypeSchema.optional(),
  status: contentStatusSchema.optional(),
  difficulty: z.union([difficultySchema, z.literal('none')]).optional(),
  yearPart: z.union([yearPartSchema, z.literal('none')]).optional(),
  attention: questionAttentionSchema.optional(),
  search: z.string().trim().max(100).optional(),
  ...pageQuery,
});

export const QUESTION_BULK_ACTIONS = [
  'publish',
  'archive',
  'accept_ai',
  'set_difficulty',
  'set_year_part',
  'set_subtopic',
] as const;

export const questionBulkInputSchema = z
  .object({
    ids: z.array(z.uuid()).min(1).max(100),
    action: z.enum(QUESTION_BULK_ACTIONS),
    difficulty: difficultySchema.optional(),
    yearPart: yearPartSchema.optional(),
    subtopicId: z.uuid().optional(),
  })
  .refine((input) => input.action !== 'set_difficulty' || input.difficulty !== undefined, {
    message: 'Choose a difficulty',
    path: ['difficulty'],
  })
  .refine((input) => input.action !== 'set_year_part' || input.yearPart !== undefined, {
    message: 'Choose middle or end of the school year',
    path: ['yearPart'],
  })
  .refine((input) => input.action !== 'set_subtopic' || input.subtopicId !== undefined, {
    message: 'Choose a subtopic',
    path: ['subtopicId'],
  });

export const questionBulkResultSchema = z.object({
  updated: z.int().min(0),
  unchanged: z.int().min(0),
  skipped: z.array(z.object({ id: z.uuid(), prompt: z.string(), reason: z.string() })),
});

export const questionListSchema = z.object({
  items: z.array(questionSummarySchema),
  total: z.int(),
  page: z.int(),
  pageSize: z.int(),
});

export const readingTextInputSchema = z.object({
  schoolGroup: schoolGroupSchema,
  title: z.string().trim().min(1).max(200),
  preview: z.string().trim().min(1).max(600),
  body: z.string().trim().min(1).max(20_000),
  imageKey: imageKeySchema.nullable(),
  imageAlt: optionalText(200).default(null),
});

export const readingTextSummarySchema = z.object({
  id: z.uuid(),
  schoolGroup: schoolGroupSchema,
  title: z.string(),
  imageKey: z.string().nullable(),
  status: contentStatusSchema,
  publishedQuestions: difficultyCountsSchema,
  totalQuestions: z.int(),
  updatedAt: timestampSchema,
});

export const readingTextDetailSchema = readingTextSummarySchema.extend({
  imageAlt: z.string().nullable(),
  preview: z.string(),
  body: z.string(),
  questions: z.array(questionSummarySchema),
  createdAt: timestampSchema,
  publishedAt: timestampSchema.nullable(),
  archivedAt: timestampSchema.nullable(),
});

export const readingTextListQuerySchema = z.object({
  schoolGroup: queryGroup,
  status: contentStatusSchema.optional(),
  search: z.string().trim().max(100).optional(),
  ...pageQuery,
});

export const readingTextListSchema = z.object({
  items: z.array(readingTextSummarySchema),
  total: z.int(),
  page: z.int(),
  pageSize: z.int(),
});

export const availabilityCellSchema = z.object({
  schoolGroup: schoolGroupSchema,
  subjectKey: z.string(),
  enabled: z.boolean(),
  published: difficultyCountsSchema,
  eligibleTexts: z.int().nullable(),
  minimumMet: z.boolean(),
  shortfall: difficultyCountsSchema.nullable(),
});

/** Games make their own sums, so a game switch has no content minimum. */
export const gameAvailabilityCellSchema = z.object({
  schoolGroup: schoolGroupSchema,
  gameKey: gameKeySchema,
  enabled: z.boolean(),
});

export const availabilitySchema = z.object({
  minimum: difficultyCountsSchema,
  recommended: difficultyCountsSchema,
  cells: z.array(availabilityCellSchema),
  games: z.array(gameAvailabilityCellSchema),
});

export const availabilityParamsSchema = z.object({
  schoolGroup: z.coerce.number().pipe(schoolGroupSchema),
  subjectKey: subjectKeySchema,
});

export const practiceAvailabilityLevelSchema = z.object({
  difficulty: difficultySchema,
  published: z.int().min(0),
  missing: z.int().min(0),
  available: z.boolean(),
});

export const practiceAvailabilitySchema = z.object({
  schoolGroup: schoolGroupSchema,
  subjectKey: subjectKeySchema,
  minimum: z.int().min(1),
  topics: z.array(
    z.object({
      id: z.uuid(),
      name: z.string(),
      subtopics: z.array(
        z.object({
          id: z.uuid(),
          name: z.string(),
          levels: z.array(practiceAvailabilityLevelSchema),
        }),
      ),
    }),
  ),
});

export const gameAvailabilityParamsSchema = z.object({
  schoolGroup: z.coerce.number().pipe(schoolGroupSchema),
  gameKey: gameKeySchema,
});

export const availabilityUpdateSchema = z.object({ enabled: z.boolean() });

export type DifficultyCounts = z.infer<typeof difficultyCountsSchema>;
export type Topic = z.infer<typeof topicSchema>;
export type TopicUsage = z.infer<typeof topicUsageSchema>;
export type Subtopic = z.infer<typeof subtopicSchema>;
export type SubtopicUsage = z.infer<typeof subtopicUsageSchema>;
export type SubtopicInput = z.infer<typeof subtopicInputSchema>;
export type Subject = z.infer<typeof subjectSchema>;
export type SubjectList = z.infer<typeof subjectListSchema>;
export type TopicInput = z.infer<typeof topicInputSchema>;
export type TopicUpdate = z.infer<typeof topicUpdateSchema>;
export type QuestionInput = z.input<typeof questionInputSchema>;
export type QuestionFields = z.output<typeof questionInputSchema>;
export type QuestionSummary = z.infer<typeof questionSummarySchema>;
export type QuestionDetail = z.infer<typeof questionDetailSchema>;
export type QuestionListQuery = Partial<QuestionListFilters>;
export type QuestionAttention = z.infer<typeof questionAttentionSchema>;
export type QuestionImage = z.infer<typeof questionImageSchema>;
export type QuestionImageInput = z.input<typeof questionImageInputSchema>;
export type QuestionBulkAction = (typeof QUESTION_BULK_ACTIONS)[number];
export type QuestionBulkInput = z.infer<typeof questionBulkInputSchema>;
export type QuestionBulkResult = z.infer<typeof questionBulkResultSchema>;
export type QuestionListFilters = z.output<typeof questionListQuerySchema>;
export type QuestionList = z.infer<typeof questionListSchema>;
export type ReadingTextInput = z.infer<typeof readingTextInputSchema>;
export type ReadingTextSummary = z.infer<typeof readingTextSummarySchema>;
export type ReadingTextDetail = z.infer<typeof readingTextDetailSchema>;
export type ReadingTextListQuery = Partial<ReadingTextListFilters>;
export type ReadingTextListFilters = z.output<typeof readingTextListQuerySchema>;
export type ReadingTextList = z.infer<typeof readingTextListSchema>;
export type AvailabilityCell = z.infer<typeof availabilityCellSchema>;
export type GameAvailabilityCell = z.infer<typeof gameAvailabilityCellSchema>;
export type Availability = z.infer<typeof availabilitySchema>;
export type PracticeAvailability = z.infer<typeof practiceAvailabilitySchema>;
